import React from "react";
import { useSelector } from "react-redux";
import { faArrowsAlt, faCircle, faCloudUploadAlt, faEraser, faExpand, faFill, faFillDrip, faPaintBrush, faPencilAlt, faRedo, faRobot, faSearchMinus, faSearchPlus, faSquareFull, faUndo } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useRouter } from "next/navigation";

import AnibotModal from "@/components/modal/components/anibot";
import SaveAnimationModal from "@/components/modal/components/save-animation";
import { useSave } from "@/hooks/editor/use-save";
import { useModal } from "@/hooks/use-modal";
import { getCanvasPane } from "@/lib/canvas/canvas-bridge";
import * as editorActions from "@/lib/state/actions/editor-actions";
import { EditorTool } from "@/lib/state/reducers/editor-reducer";
import { animationSelector } from "@/lib/state/selectors/animation-selector";
import { editorSelector } from "@/lib/state/selectors/editor-selector";
import { useAppDispatch } from "@/lib/state/store";

import EditorButton from "../editor-button";
import SliderControl from "../slider-control";

import "./_toolbar.scss";

const Toolbar = () => {
  const dispatch = useAppDispatch();
  const animation = useSelector(animationSelector);
  const editor = useSelector(editorSelector);
  const router = useRouter();
  const { openModal } = useModal();
  const { saveAnimation } = useSave();

  const { currentTool, undoStack, currentBrushSize, opacity, mode, redoStack } =
    editor;

  const onButtonPressed = (type: string | undefined) => {
    dispatch(editorActions.onToolChanged(type as EditorTool));
  };

  const onZoomOutPressed = () => {
    getCanvasPane().zoomOut();
  };
  const onZoomInPressed = () => {
    getCanvasPane().zoomIn();
  };
  const onResetZoomPressed = () => {
    getCanvasPane().zoomToFit();
  };

  const onSavePressed = () => {
    if (!animation.url) {
      openModal(() => <SaveAnimationModal />);
      return;
    }
    saveAnimation();
  };

  const className = `editor-toolbar ${mode !== "playback" ? "" : "editor-toolbar--hidden"
    }`;

  const currentColour = animation.palette[editor.currentColourIndex];

  return (
    <ol className={className}>
      <li>
        <ol className="editor-toolbar__group">
          <li className="editor-toolbar__item">
            <EditorButton
              label={<FontAwesomeIcon icon={faSearchMinus} />}
              onButtonPressed={onZoomOutPressed}
            />
          </li>
          <li className="editor-toolbar__item">
            <EditorButton
              label={<FontAwesomeIcon icon={faExpand} />}
              onButtonPressed={onResetZoomPressed}
            />
          </li>
          <li className="editor-toolbar__item">
            <EditorButton
              label={<FontAwesomeIcon icon={faSearchPlus} />}
              onButtonPressed={onZoomInPressed}
            />
          </li>
        </ol>
      </li>
      <li>
        <ol className="editor-toolbar__group">
          <li className="editor-toolbar__item">
            <EditorButton
              type="pencil"
              onButtonPressed={onButtonPressed}
              label={<FontAwesomeIcon icon={faPencilAlt} />}
              active={currentTool === "pencil"}
            />
          </li>
          <li className="editor-toolbar__item">
            <EditorButton
              type="paint"
              onButtonPressed={onButtonPressed}
              label={<FontAwesomeIcon icon={faPaintBrush} />}
              active={currentTool === "paint"}
            />
          </li>
          <li className="editor-toolbar__item">
            <EditorButton
              type="fill"
              onButtonPressed={onButtonPressed}
              label={<FontAwesomeIcon icon={faFillDrip} />}
              active={currentTool === "fill"}
            />
          </li>
          <li className="editor-toolbar__item">
            <EditorButton
              type="line"
              onButtonPressed={onButtonPressed}
              label={<strong>|</strong>}
              active={currentTool === "line"}
            />
          </li>
          <li className="editor-toolbar__item">
            <EditorButton
              type="circle"
              onButtonPressed={onButtonPressed}
              label={<FontAwesomeIcon icon={faCircle} />}
              active={currentTool === "circle"}
            />
          </li>
          <li className="editor-toolbar__item">
            <EditorButton
              type="rectangle"
              onButtonPressed={onButtonPressed}
              label={<FontAwesomeIcon icon={faSquareFull} />}
              active={currentTool === "rectangle"}
            />
          </li>
          <li className="editor-toolbar__item">
            <EditorButton
              type="erase"
              onButtonPressed={onButtonPressed}
              label={<FontAwesomeIcon icon={faEraser} />}
              active={currentTool === "erase"}
            />
          </li>
          <li className="editor-toolbar__item">
            <EditorButton
              type="moveLayer"
              onButtonPressed={onButtonPressed}
              label={<FontAwesomeIcon icon={faArrowsAlt} />}
              active={currentTool === "moveLayer"}
            />
          </li>
          <li className="editor-toolbar__item">
            <EditorButton
              onButtonPressed={() => openModal(() => <AnibotModal />)}
              label={<FontAwesomeIcon icon={faRobot} />}
            />
          </li>
        </ol>
      </li>
      <li className="w-full">
        <ol className="editor-toolbar__group">
          <li className="flex flex-row items-center">
            <span className="icon icon--brush-small m-2">
              <FontAwesomeIcon icon={faCircle} size="xs" />
            </span>
            <SliderControl
              min={1}
              max={30}
              decimals={0}
              onChange={(size) =>
                dispatch(editorActions.onBrushSizeChanged(size))
              }
              defaultValue={currentBrushSize}
            />
            <span className="icon icon--brush-large m-2">
              <FontAwesomeIcon icon={faCircle} size="lg" />
            </span>
          </li>
        </ol>
        <ol className="editor-toolbar__group">
          <li className="flex flex-row items-center">
            <span className="icon editor-toolbar-icon__transparent m-2"></span>
            <SliderControl
              min={0}
              max={1}
              decimals={2}
              onChange={(opacity) =>
                dispatch(editorActions.onOpacityChanged(opacity))
              }
              defaultValue={opacity}
            />
            <span
              className="icon editor-toolbar-icon__solid m-2"
              style={{ background: currentColour }}
            ></span>
          </li>
        </ol>
      </li>
      <li>
        <ol className="editor-toolbar__group">
          <li className="editor-toolbar__item">
            <EditorButton
              type="undo"
              onButtonPressed={() => dispatch(editorActions.undo())}
              label={<FontAwesomeIcon icon={faUndo} />}
              transient={true}
              disabled={undoStack.length === 0}
            />
          </li>
          <li className="editor-toolbar__item">
            <EditorButton
              type="redo"
              onButtonPressed={() => dispatch(editorActions.redo())}
              label={<FontAwesomeIcon icon={faRedo} />}
              transient={true}
              disabled={redoStack.length === 0}
            />
          </li>
          <li className="editor-toolbar__item">
            <EditorButton
              type="save"
              onButtonPressed={onSavePressed}
              label={<FontAwesomeIcon icon={faCloudUploadAlt} />}
              transient={true}
            />
          </li>
        </ol>
      </li>
    </ol>
  );
};

export default Toolbar;
